import { LeftSidebarView, ViewBody } from 'components/Sidebar'
import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import KanBoIcon from 'components/KanBoIcon'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { ModalBoardContext } from 'components/Modal'
import { useBoardContext } from 'dataProviders/Board'
import {
  ListGroup,
  ControlLabel,
  FormControl,
  FormGroup,
} from 'react-bootstrap'
import {
  ListItem
} from 'components/List'
import SelectedDocumentTemplates from '../SelectedDocumentTemplates'
import { useDispatch, useSelector } from 'react-redux'
import requestChangeDocumentFolderCreatorPath from '../sagas/requestChangeDocumentFolderCreatorPath'
import requestChangeDocumentFolderCreatorSettings from '../sagas/requestChangeDocumentFolderCreatorSettings'
import { useCardContext } from 'dataProviders/Card'
import styled from 'styled-components'
import DocumentItem from 'routes/Board/DocumentList/DocumentItem'
import Documents from 'routes/Board/DocumentList/Documents'
import Button from 'components/Button'
import SelectedDocumentFolder from '../SelectedDocumentFolder'
import Toggle from 'components/Toggle'
import EditableModal from 'components/EditableModal'
import { DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_PREFIX_LENGTH } from 'const/limits'

const SelectButton = styled(Button)`
  float: right;
`

const FolderNamePrefix = styled(ListItem)`
  margin: 0 -10px;
`

const NotSet = styled.span`
  opacity: 0.5;
`

const SHOW_MAX_OPTIONS = [5, 10, 15, 20]

const DocumentFolderCreatorSettings: React.FC<LeftSidebarProps & {
  elementId: number;
}> = ({
  leftSidebar,
  elementId,
}) => {
  const intl = useIntl()

  const dispatch = useDispatch()

  const boardContext = useBoardContext()

  const cardContext = useCardContext()

  const name = useSelector<any, string>(state => state['view-card'].elements.elementsById[elementId]?.name)

  const documentSourceId = useSelector<any, number>(state => state['view-card'].elements.elementsById[elementId]?.documentSourceId)

  const path = useSelector<any, string>(state => state['view-card'].elements.elementsById[elementId]?.path)

  const settings = useSelector<any, any>(state => state['view-card'].elements.elementsById[elementId]?.settings)

  const handleEditableValidate = useCallback(({ name }) => {
    const errors: any = {}

    if (name && name.length > DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_PREFIX_LENGTH) {
      errors.name = intl.formatMessage({ id: 'DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_PREFIX_TOO_LONG' }, { limit: DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_PREFIX_LENGTH })
    }

    return errors
  }, [])

  const handleEditableSubmit = useCallback(({ name: newName }) => {
    const trimmedNewName = newName.trim()

    if (trimmedNewName !== settings?.FolderNamePrefix) {
      dispatch(requestChangeDocumentFolderCreatorSettings({
        cardId: cardContext.id,
        documentFolderCreatorId: elementId,
        settings: {
          FolderNamePrefix: trimmedNewName,
        }
      }))
    }
  }, [settings?.FolderNamePrefix])

  const handleAppendCardIdChange = useCallback(appendCardId => {
    dispatch(requestChangeDocumentFolderCreatorSettings({
      cardId: cardContext.id,
      documentFolderCreatorId: elementId,
      settings: {
        AppendCardId: appendCardId,
      }
    }))
  }, [elementId, cardContext.id])

  const handleShowMaxItemsChange = useCallback(event => {
    dispatch(requestChangeDocumentFolderCreatorSettings({
      cardId: cardContext.id,
      documentFolderCreatorId: elementId,
      settings: {
        FolderSettings: {
          ShowMaxItems: Number(event.target.value)
        }
      }
    }))
  }, [elementId, cardContext.id])

  const handleSelectedFolderClick = useCallback(() => {
    leftSidebar.push(() =>
      <ModalBoardContext boardId={boardContext.id}>
        <LeftSidebarView
          title={<FormattedMessage id='DOCUMENT_FOLDER_SELECT_SOURCE' />}
          subtitle={name}
        >
          <Documents
            boardId={boardContext.id}
            documentComponent={(props) =>
              <DocumentItem
                {...props}
                name={
                  (
                    props.type === 'source' ||
                    props.type === 'folder'
                  ) ? (
                    <>
                      {props.name}
                      <SelectButton
                        bsSize='xsmall'
                        bsStyle='primary'
                        onClick={(event) => {
                          event.stopPropagation()

                          leftSidebar.pop()

                          dispatch(requestChangeDocumentFolderCreatorPath({
                            cardId: cardContext.id,
                            documentFolderCreatorId: elementId,
                            documentSourceId: props.documentSourceId,
                            path: props.type === 'folder' ? props.url : undefined,
                          }))
                        }}
                      >
                        <FormattedMessage id='DOCUMENT_FOLDER_SELECT_SOURCE_SELECT' />
                      </SelectButton>
                    </>
                    ) : props.name
                }
              />
            }
          />
        </LeftSidebarView>
      </ModalBoardContext>
    )
  }, [elementId, boardContext.id, cardContext.id])

  const handleSelectedDocumentTemplatesClick = useCallback(() => {
    leftSidebar.pop()

    leftSidebar.push(() =>
      <ModalBoardContext boardId={boardContext.id}>
        <SelectedDocumentTemplates documentListId={elementId} />
      </ModalBoardContext>
    )
  }, [elementId, boardContext.id])

  return (
    <LeftSidebarView
      title={<FormattedMessage id='SETTINGS' />}
      subtitle={name}
    >
      <ViewBody>
        <FormGroup>
          <ControlLabel><FormattedMessage id='DOCUMENT_FOLDER_CREATOR_SELECTED_SOURCE' /></ControlLabel>
          <SelectedDocumentFolder
            documentSourceId={documentSourceId}
            path={path}
            onClick={handleSelectedFolderClick}
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel><FormattedMessage id='DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_PREFIX' /></ControlLabel>
          <EditableModal
            initialValues={{ name: settings?.FolderNamePrefix }}
            validate={handleEditableValidate}
            onSubmit={handleEditableSubmit}
          >
            <FolderNamePrefix>
              {settings?.FolderNamePrefix ? (
                settings?.FolderNamePrefix
              ) : (
                <NotSet>
                  <FormattedMessage id='DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_PREFIX_EMPTY' />
                </NotSet>
              )}
            </FolderNamePrefix>
          </EditableModal>
        </FormGroup>
        <FormGroup>
          <ControlLabel><FormattedMessage id='DOCUMENT_FOLDER_CREATOR_SETTINGS_FOLDER_NAME_APPEND_CARD_ID' /></ControlLabel>
          <div>
            <Toggle
              checked={settings?.AppendCardId}
              onChange={handleAppendCardIdChange}
            />
          </div>
        </FormGroup>
        <FormGroup>
          <ControlLabel><FormattedMessage id='DOCUMENT_FOLDER_SETTINGS_SHOW_MAX_ITEMS' /></ControlLabel>
          <FormControl
            value={settings?.FolderSettings.ShowMaxItems}
            onChange={handleShowMaxItemsChange}
            componentClass='select'
          >
            {SHOW_MAX_OPTIONS.map(value =>
              <option
                key={value}
                value={value}
              >
                {value}
              </option>
            )}
          </FormControl>
        </FormGroup>
      </ViewBody>
      <ListGroup>
        <ListItem
          borderedIcon
          icon={<KanBoIcon fixedWidth name='kanbo-16-document-template-source' />}
          onClick={handleSelectedDocumentTemplatesClick}
        >
          <FormattedMessage id='DOCUMENT_LIST_SETTINGS_ALLOWED_DOCUMENT_TEMPLATES' />
        </ListItem>
      </ListGroup>
    </LeftSidebarView>
  )
}

export default withLeftSidebar(DocumentFolderCreatorSettings)
